import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useBooking } from "@/hooks/useBooking";
import { useAuth } from "@/hooks/useAuth";
import { formatKES } from "@/lib/format";
import { formatLongDate } from "@/lib/date";
import { isValidKenyanPhone, normalizePhone } from "@/lib/pricing";
import { supabase } from "@/lib/supabase";

interface MpesaResponse {
  success?: boolean;
  code?: string;
  message?: string;
  customerMessage?: string;
  checkoutRequestId?: string;
}

export default function Checkout() {
  const { bookingId } = useParams();
  const id = bookingId ? Number(bookingId) : null;
  const { booking, vehicle, loading, error } = useBooking(id);
  const { user, profile } = useAuth();

  const [phone, setPhone] = useState(profile?.phone || "");
  const [status, setStatus] = useState<"idle" | "loading" | "pending" | "error">("idle");
  const [message, setMessage] = useState("");
  const [mpesaReady, setMpesaReady] = useState(true);

  async function handlePay(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!booking) return;

    if (!isValidKenyanPhone(phone)) {
      setStatus("error");
      setMessage("Please enter a valid M-Pesa number, e.g. 0712 345 678.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const { data, error: fnError } = await supabase.functions.invoke<MpesaResponse>("mpesa-stk-push", {
        body: {
          phone: normalizePhone(phone),
          amount: Number(booking.total_price ?? 0),
          bookingId: booking.id,
          accountReference: `GARI-${booking.id}`,
          description: vehicle ? `Gari booking — ${vehicle.make} ${vehicle.model}` : "Gari booking",
        },
      });

      if (fnError) throw fnError;

      if (data?.code === "MPESA_NOT_CONFIGURED") {
        setMpesaReady(false);
        setStatus("error");
        setMessage(data.message || "M-Pesa is not connected yet.");
        return;
      }

      if (!data?.success) {
        setStatus("error");
        setMessage(data?.message || "M-Pesa could not start the payment. Please try again.");
        return;
      }

      setStatus("pending");
      setMessage(data.customerMessage || "Check your phone and enter your M-Pesa PIN to confirm the payment.");
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "Something went wrong starting the payment.");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto w-full max-w-5xl px-4 pt-32 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_400px]">
            <div className="h-72 w-full animate-pulse rounded-2xl bg-background-100" />
            <div className="h-96 w-full animate-pulse rounded-2xl bg-background-100" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <Navbar />
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 pt-40 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
            <i className="ri-secure-payment-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
            {error || "Booking not found"}
          </h1>
          <Link
            to="/browse"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
          >
            <i className="ri-search-eye-line"></i> Browse cars
          </Link>
        </div>
      </div>
    );
  }

  const vehicleTitle = vehicle ? `${vehicle.make} ${vehicle.model}` : "Your Gari car";
  const vehicleImage = vehicle?.images?.[0]?.url;
  const paid = booking.status && booking.status !== "pending";

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 md:px-8 md:pt-36">
        <div className="max-w-2xl">
          <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Secure checkout</span>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            Confirm your booking
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            Pay with M-Pesa to lock in your dates. Booking reference{" "}
            <span className="font-medium text-foreground-900">#{booking.id}</span>.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_420px]">
          <div className="space-y-6">
            <div className="overflow-hidden rounded-2xl surface-card">
              <div className="flex flex-col gap-4 p-5 sm:flex-row">
                {vehicleImage && (
                  <div className="h-32 w-full shrink-0 overflow-hidden rounded-xl bg-background-100 sm:h-24 sm:w-36">
                    <img
                      src={vehicleImage}
                      alt={vehicleTitle}
                      title={`${vehicleTitle} — Gari booking`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">{vehicleTitle}</h2>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground-600">
                    <i className="ri-map-pin-line text-foreground-500"></i>
                    {booking.pickup_location || vehicle?.city}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i> {formatLongDate(booking.start_date)} → {formatLongDate(booking.end_date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-time-line"></i> {booking.days} {booking.days === 1 ? "day" : "days"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2.5 border-t border-background-200/70 p-5">
                <div className="flex items-center justify-between text-sm text-foreground-700">
                  <span>Daily rate × {booking.days}</span>
                  <span className="text-foreground-950">{formatKES(booking.daily_rate)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-foreground-700">
                  <span>Subtotal</span>
                  <span className="text-foreground-950">{formatKES(booking.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-foreground-700">
                  <span>Gari service fee</span>
                  <span className="text-foreground-950">{formatKES(booking.service_fee)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-background-200/70 pt-3">
                  <span className="font-heading text-base font-semibold text-foreground-950">Total to pay</span>
                  <span className="font-heading text-xl font-semibold text-primary-500">
                    {formatKES(booking.total_price)}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <i className="ri-shield-check-line text-accent-400"></i> Your booking is protected
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                Payment is held securely until your trip is confirmed. Cancel up to 24 hours before pick-up for a full
                refund, straight back to your M-Pesa.
              </p>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl surface-card p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-2xl text-background-50">
                  <i className="ri-smartphone-line"></i>
                </span>
                <div>
                  <p className="font-heading text-base font-semibold text-foreground-950">Pay with M-Pesa</p>
                  <p className="text-xs text-foreground-500">Fast, safe, and local</p>
                </div>
              </div>

              {paid ? (
                <div className="mt-6 rounded-xl border border-accent-500/30 bg-accent-500/10 p-5">
                  <p className="flex items-center gap-2 text-sm font-medium text-accent-300">
                    <i className="ri-checkbox-circle-fill text-lg"></i> This booking is {booking.status}
                  </p>
                  <p className="mt-2 text-sm text-foreground-600">
                    No further payment is needed. Your host will be in touch about pick-up.
                  </p>
                  <Link
                    to="/browse"
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
                  >
                    <i className="ri-search-eye-line"></i> Browse more cars
                  </Link>
                </div>
              ) : status === "pending" ? (
                <div className="mt-6 rounded-xl border border-primary-500/30 bg-primary-500/10 p-5">
                  <p className="flex items-center gap-2 text-sm font-medium text-primary-400">
                    <i className="ri-loader-4-line animate-spin text-lg"></i> Waiting for your PIN
                  </p>
                  <p className="mt-2 text-sm text-foreground-600">{message}</p>
                  <p className="mt-3 text-xs text-foreground-500">
                    Didn&apos;t get the prompt? Check your signal, then try again below.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-900"
                  >
                    <i className="ri-refresh-line"></i> Try again
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePay} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="mpesa-phone" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
                      M-Pesa number
                    </label>
                    <div className="relative">
                      <i className="ri-phone-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
                      <input
                        id="mpesa-phone"
                        type="tel"
                        name="phone"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        placeholder="0712 345 678"
                        autoComplete="tel"
                        required
                        className="w-full rounded-lg border border-background-200/70 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                    <p className="mt-2 text-xs text-foreground-500">
                      You&apos;ll get an STK prompt on this number — enter your PIN to pay.
                    </p>
                  </div>

                  <div className="flex items-center justify-between rounded-lg bg-background-200/40 px-4 py-3">
                    <span className="text-sm text-foreground-600">Amount</span>
                    <span className="font-heading text-lg font-semibold text-foreground-950">
                      {formatKES(booking.total_price)}
                    </span>
                  </div>

                  {status === "error" && message && (
                    <p className="flex items-start gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-3 text-sm text-secondary-300">
                      <i className="ri-error-warning-line mt-0.5"></i> {message}
                    </p>
                  )}

                  {!mpesaReady && (
                    <p className="flex items-start gap-2 rounded-lg border border-primary-500/30 bg-primary-500/10 px-3.5 py-3 text-xs text-foreground-600">
                      <i className="ri-information-line mt-0.5 text-primary-400"></i>
                      M-Pesa is being connected. Your booking is saved as pending — the payment step will go live as
                      soon as the account is linked.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> Sending prompt
                      </>
                    ) : (
                      <>
                        <i className="ri-secure-payment-line"></i> Pay {formatKES(booking.total_price)}
                      </>
                    )}
                  </button>

                  {user ? (
                    <p className="text-center text-xs text-foreground-500">
                      Paying as {profile?.full_name || user.email}
                    </p>
                  ) : (
                    <p className="text-center text-xs text-foreground-500">
                      <Link to="/auth" className="text-primary-500 hover:text-primary-400">
                        Sign in
                      </Link>{" "}
                      to keep track of your trips
                    </p>
                  )}
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}